import { css } from "@emotion/css";
import { Level2OuterPageLayout, type PageSideNavLink } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import React, { useMemo } from "react";
import ArgoCDAvatar from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDAvatar.svg";
import { ArgoCDGatewayHealthStatusIcon } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthStatusIcon";
import { useArgoCDIntegration } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDIntegration";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
type ArgoCDInstanceLayoutProps = {
    instanceId: string;
    children: (gateway: ArgoCDGatewayResource) => ReactNode;
};
const styles = {
    connectivityContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[6], //consistent with Machine connectivity nav link space
        alignItems: "center",
    }),
};
export const ArgoCDInstanceLayout = (props: ArgoCDInstanceLayoutProps) => {
    const { instanceId, children } = props;
    const { result: gateway, isLoading, error } = useInlineStatusQuery((repo) => repo.ArgoCDGateways.get(instanceId), [instanceId], "Loading ArgoCDGateway");
    const argoCDIntegrationEnabled = useArgoCDIntegration();
    const createSideNavLink = useCreatePageSideNavLink();
    const navLinks: Array<PageSideNavLink> = useMemo(() => {
        if (!gateway) {
            return [];
        }
        const connectivityHeader = (<span className={styles.connectivityContainer}>
                <span>Gateway Connectivity</span>
                <ArgoCDGatewayHealthStatusIcon healthStatus={gateway.HealthStatus} title={gateway.HealthStatus} iconSize={"small"}/>
            </span>);
        return [
            createSideNavLink("Settings", links.argoCDInstanceSettingsPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
            createSideNavLink("Applications", links.argoCDInstanceApplicationsPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
            createSideNavLink(connectivityHeader, links.argoCDInstanceConnectivityPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
        ];
    }, [createSideNavLink, gateway]);
    if (!argoCDIntegrationEnabled) {
        return <RedirectAs404 />;
    }
    //if we are loading or have an error, this does both!
    if (isLoading || error) {
        const errors = error !== null ? getErrorsFromError(error) : undefined;
        return <SkeletonLoadingLayout errors={errors}/>;
    }
    //we didn't load anything but didn't have an error :shrug:
    // noinspection PointlessBooleanExpressionJS
    if (gateway === null) {
        return <RedirectAs404 />;
    }
    const rootLink = links.argoCDInstancesPage.generateUrl({ spaceId: gateway.SpaceId });
    return (<main>
            <Level2OuterPageLayout navItems={navLinks} header={{
            breadcrumbs: [{ label: "Argo CD Instances", pageUrl: rootLink }],
            title: gateway.Name,
            logo: { href: ArgoCDAvatar, accessibleName: "Argo CD logo" },
        }}>
                {children(gateway)}
            </Level2OuterPageLayout>
        </main>);
};
